import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import {
  Box,
  Container,
  Stack,
  Typography,
  Button,
  TextField,
  InputAdornment,
  useTheme
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon
} from '@mui/icons-material';

import FilterSelectInput from 'src/common/FilterSelectInput';
import DispenserList from 'src/components/dispensers/DispenserListTable';
import NewDispenser from 'src/components/dispensers/NewDispenser';
import NewMap from 'src/components/dispensers/NewMap';
import useAuth from 'src/context/AuthContext';
import { hasAdminLikeAccess } from 'src/utils/getInitials';
import { APIService } from 'src/api/api';
import useAdminSiteFilter from 'src/hooks/useAdminSiteFilter';

const Dispensers = ({ location }) => {
  const theme = useTheme();
  const { getUser } = useAuth();
  const {
    isAdmin,
    siteFilter,
    setSiteFilter,
    siteOptions
  } = useAdminSiteFilter();
  const hasAdminAccess = hasAdminLikeAccess(getUser);

  const [openModal, setOpenModal] = useState(false);
  const [openMapModal, setOpenMapModal] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await APIService.getSettings();
        setSettings(res?.result || null);
      } catch (err) {
        console.log('Settings API error:', err);
      }
    };

    fetchSettings();
  }, []);

  const showDevButton = settings?.environment === 'development';

  return (
    <>
      <Helmet>
        <title>Dispensers | Retrack FMS</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: '#FFFFFF',
          minHeight: 'calc(100vh - 64px)',
          height: { xs: 'auto', md: 'calc(100vh - 64px)' },
          overflow: { xs: 'auto', md: 'hidden' },
          py: 2
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0
          }}
        >

          {/* --- Header Section (Same as Sites) --- */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
            spacing={3}
            sx={{ py: 2, mb: 2, flexShrink: 0 }}
          >
            {/* Title + Subtitle */}
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  color: '#111827',
                  letterSpacing: '-0.02em',
                  mb: 0.5
                }}
              >
                DISPENSERS
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontWeight: 500 }}
              >
                Manage fuel dispensers and configurations.
              </Typography>
            </Box>

            {/* Actions */}
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', md: 'center' }}
              useFlexGap
              flexWrap={{ xs: 'nowrap', md: 'wrap', lg: 'nowrap' }}
              sx={{ width: { xs: '100%', md: 'auto' } }}
            >
              <TextField
                placeholder="Search dispensers..."
                size="small"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                sx={{
                  width: {
                    xs: '100%', sm: 240, md: 260, xl: 320
                  },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: '#F9FAFB',
                    '& fieldset': { borderColor: '#E5E7EB' },
                    '&:hover fieldset': { borderColor: '#D1D5DB' },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main
                    }
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                    </InputAdornment>
                  )
                }}
              />

              {isAdmin && (
                <FilterSelectInput
                  label="Site"
                  name="site"
                  value={siteFilter}
                  data={siteOptions}
                  onInputChange={setSiteFilter}
                  sx={{ width: { xs: '100%', sm: 170, md: 180 }, mr: 0 }}
                />
              )}

              {hasAdminAccess && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenModal(true)}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  minHeight: 40,
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  '&:hover': {
                    boxShadow: '0 6px 16px rgba(0,0,0,0.15)'
                  }
                }}
              >
                Add Dispenser
              </Button>
              )}

              {showDevButton && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => setOpenMapModal(true)}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  minHeight: 40,
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}
              >
                Map Cards
              </Button>
              )}
            </Stack>
          </Stack>

          {/* --- Modals --- */}
          <NewDispenser
            open={openModal}
            handleClose={() => setOpenModal(false)}
          />

          <NewMap
            open={openMapModal}
            handleClose={() => setOpenMapModal(false)}
          />

          {/* --- Table Section --- */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'hidden'
            }}
          >
            <DispenserList
              refreshData={location.state?.fromDashboard}
              searchText={searchText}
              siteFilter={siteFilter}
            />
          </Box>

        </Container>
      </Box>
    </>
  );
};

Dispensers.propTypes = {
  location: PropTypes.object
};

export default Dispensers;
